import React, { useCallback, useMemo, useState } from 'react';
import Button from 'shared/components/UC/Button';
import Steps from 'shared/components/UC/Steps';
import Switch from 'shared/components/UC/Switch';
import Toast from 'shared/components/Toast';
import {
  ImportIssueCode,
  ImportOutcome,
  ImportPreview,
} from '../../../types/bulkAssignment';
import bulkAssignmentService, {
  getContestRules,
  resolveEvaluatorLoads,
  summarizePreview,
} from '../../../services/bulkAssignmentService';
import { IMPORT_ISSUE_CATALOG } from './importIssues';
import ImportConfirmModal from './ImportConfirmModal';
import ImportDropzone from './ImportDropzone';
import ImportIssuesPanel from './ImportIssuesPanel';
import ImportPreviewTable, { PreviewView } from './ImportPreviewTable';
import ImportResult from './ImportResult';
import ImportSummary from './ImportSummary';
import styles from './BulkAssignmentTab.module.css';

const STEPS = ['Cargar archivo', 'Revisar asignaciones', 'Resultado'];

interface BulkAssignmentTabProps {
  formTemplateId: string | null;
  onGoToApplications: () => void;
  onGoToEvaluations: () => void;
  /** Abre Evaluaciones ya en la sub-vista por evaluador, filtrada. */
  onGoToPendingInvitations: () => void;
  /** Lets the dashboard reload its lists once the assignments exist. */
  onImported: () => void;
}

const BulkAssignmentTab: React.FC<BulkAssignmentTabProps> = ({
  formTemplateId,
  onGoToApplications,
  onGoToEvaluations,
  onGoToPendingInvitations,
  onImported,
}) => {
  const rules = getContestRules();

  const [step, setStep] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [discarded, setDiscarded] = useState<Set<string>>(new Set());
  const [view, setView] = useState<PreviewView>('application');
  const [issueFilter, setIssueFilter] = useState<ImportIssueCode | null>(null);
  const [sendInvitations, setSendInvitations] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  const totals = useMemo(
    () => (preview ? summarizePreview(preview, discarded) : null),
    [preview, discarded]
  );
  const evaluatorLoads = useMemo(
    () => (preview ? resolveEvaluatorLoads(preview, discarded) : []),
    [preview, discarded]
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      setAnalyzing(true);
      try {
        const result = await bulkAssignmentService.analyzeFile(
          { name: file.name, size: file.size },
          formTemplateId
        );
        setPreview(result);
        setDiscarded(new Set());
        setIssueFilter(null);
        setView('application');
        setStep(1);
      } catch (error) {
        setToast({
          message:
            error instanceof Error ? error.message : 'No se pudo analizar el archivo',
          type: 'error',
        });
      } finally {
        setAnalyzing(false);
      }
    },
    [formTemplateId]
  );

  const handleToggleMatch = useCallback((key: string) => {
    setDiscarded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleToggleRow = useCallback(
    (folio: string) => {
      if (!preview) return;
      const row = preview.rows.find((r) => r.folio === folio);
      if (!row) return;
      const keys = row.matches.filter((m) => m.importable).map((m) => m.key);
      setDiscarded((prev) => {
        const next = new Set(prev);
        const allDiscarded = keys.every((key) => next.has(key));
        keys.forEach((key) => (allDiscarded ? next.delete(key) : next.add(key)));
        return next;
      });
    },
    [preview]
  );

  // An evaluator-scoped issue only makes sense in the "Por evaluador" view.
  const handleIssueFilterChange = useCallback((code: ImportIssueCode | null) => {
    setIssueFilter(code);
    if (code) setView(IMPORT_ISSUE_CATALOG[code].scope === 'evaluator' ? 'evaluator' : 'application');
  }, []);

  const handleApply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const result = await bulkAssignmentService.applyImport(preview, {
        sendInvitations,
        discarded,
      });
      setOutcome(result);
      setUndone(false);
      setConfirmOpen(false);
      setStep(2);
      onImported();
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : 'No se pudo aplicar la asignación',
        type: 'error',
      });
    } finally {
      setApplying(false);
    }
  };

  const handleUndo = async () => {
    if (!outcome) return;
    setUndoing(true);
    try {
      const { removed, kept } = await bulkAssignmentService.undoImport(outcome.batchId);
      setUndone(true);
      onImported();
      setToast({
        message:
          kept > 0
            ? `Se revirtieron ${removed} asignaciones. ${kept} ya tenían respuesta y se mantuvieron.`
            : `Se revirtieron ${removed} asignaciones.`,
        type: 'success',
      });
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : 'No se pudo deshacer la importación',
        type: 'error',
      });
    } finally {
      setUndoing(false);
    }
  };

  const handleStartOver = () => {
    setStep(0);
    setPreview(null);
    setOutcome(null);
    setUndone(false);
    setDiscarded(new Set());
    setIssueFilter(null);
    setSendInvitations(false);
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.intro}>
        <h2 className="uc-h4">Asignación masiva de evaluadores</h2>
        <p className={`uc-p-body ${styles.subtitle}`}>
          Importa el archivo de resultado del match para asignar evaluadores a todas
          las postulaciones admisibles de una sola vez.
        </p>
      </header>

      <Steps steps={STEPS} currentIdx={step} />

      {step === 0 && (
        <ImportDropzone
          quotaPerEvaluator={rules.quotaPerEvaluator}
          minEvaluatorsPerApplication={rules.minEvaluatorsPerApplication}
          analyzing={analyzing}
          onFileSelected={handleFileSelected}
        />
      )}

      {step === 1 && preview && totals && (
        <>
          <div className={styles.fileBar}>
            <span className="uc-p-body-small">
              Archivo analizado: <strong>{preview.fileName}</strong> ·{' '}
              {preview.totalMatchesInFile} matches
            </span>
            <Button type="button" variant="text" size="sm" onClick={handleStartOver}>
              Cambiar archivo
            </Button>
          </div>

          <ImportSummary
            totals={totals}
            minEvaluatorsPerApplication={preview.minEvaluatorsPerApplication}
          />

          <p className={`uc-p-body ${styles.reassurance}`}>
            Puedes importar aunque queden casos sin resolver. Las postulaciones
            incompletas quedan señaladas en Postulaciones y en Evaluaciones por
            postulación, donde puedes completarlas con Gestionar Evaluadores.
          </p>

          <ImportIssuesPanel
            totals={totals}
            activeFilter={issueFilter}
            onFilterChange={handleIssueFilterChange}
          />

          <ImportPreviewTable
            preview={preview}
            discarded={discarded}
            evaluators={evaluatorLoads}
            view={view}
            onViewChange={setView}
            issueFilter={issueFilter}
            onIssueFilterChange={handleIssueFilterChange}
            onToggleMatch={handleToggleMatch}
            onToggleRow={handleToggleRow}
          />

          <div className={styles.actionBar}>
            <Button type="button" variant="text" onClick={handleStartOver}>
              Atrás
            </Button>
            <div className={styles.actionBarRight}>
              <span className={`uc-p-body-small ${styles.tally}`}>
                {totals.toCreate} asignaciones se crearán
                {totals.discarded > 0 && ` · ${totals.discarded} descartadas`}
              </span>
              <Switch
                id="bulk-send-invitations"
                label="Enviar invitaciones por correo"
                checked={sendInvitations}
                onChange={(e) => setSendInvitations(e.target.checked)}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => setConfirmOpen(true)}
                disabled={totals.toCreate === 0}
              >
                Asignar evaluadores
              </Button>
            </div>
          </div>

          <ImportConfirmModal
            isOpen={confirmOpen}
            applying={applying}
            totals={totals}
            sendInvitations={sendInvitations}
            minEvaluatorsPerApplication={preview.minEvaluatorsPerApplication}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={handleApply}
          />
        </>
      )}

      {step === 2 && outcome && (
        <ImportResult
          outcome={outcome}
          minEvaluatorsPerApplication={rules.minEvaluatorsPerApplication}
          undoing={undoing}
          undone={undone}
          onUndo={handleUndo}
          onDownload={() => bulkAssignmentService.exportOutcome(outcome)}
          onGoToApplications={onGoToApplications}
          onGoToEvaluations={onGoToEvaluations}
          onGoToPendingInvitations={onGoToPendingInvitations}
          onStartOver={handleStartOver}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default BulkAssignmentTab;
